<template>
  <div class="category-select">
    <!-- 智能推荐提示 -->
    <div v-if="recommendation && recommendation.categoryName" class="recommend-hint" :class="'confidence-' + getConfidenceLevel()">
      <div class="recommend-animation">
        <span class="robot-icon">🤖</span>
        <span class="sparkle">✨</span>
      </div>
      <div class="recommend-content">
        <div class="recommend-title">
          <span class="ai-badge">{{ $t('category.aiRecommend') }}</span>
          <span class="recommend-text">
            <strong>{{ recommendation.icon }} {{ recommendation.categoryName }}</strong>
          </span>
        </div>
        <div class="recommend-details">
          <span class="confidence-bar">
            <span class="confidence-fill" :style="{width: recommendation.confidence + '%'}"></span>
          </span>
          <span class="confidence-text" v-if="recommendation.confidence >= 80">{{ $t('category.highMatch') }} {{ Math.round(recommendation.confidence) }}%</span>
          <span class="confidence-text" v-else-if="recommendation.confidence >= 50">{{ $t('category.mediumMatch') }} {{ Math.round(recommendation.confidence) }}%</span>
          <span class="confidence-text" v-else>{{ $t('category.lowMatch') }} {{ Math.round(recommendation.confidence) }}%</span>
        </div>
        <div class="matched-keywords" v-if="recommendation.matchedKeywords && recommendation.matchedKeywords.length > 0">
          {{ $t('category.matchedKeywords') }}
          <span v-for="(keyword, idx) in recommendation.matchedKeywords" :key="idx" class="keyword-tag">{{ keyword }}</span>
        </div>
      </div>
      <button type="button" class="change-btn" @click="manualMode = true">{{ $t('category.manualSelect') }}</button>
    </div>

    <!-- 分类选择器 - 始终显示 -->
    <div class="category-wrapper">
      <div class="category-dropdown">
        <select 
          v-model="localValue"
          @change="$emit('input', localValue)"
          class="category-input"
          :disabled="disabled"
        >
          <option value="">{{ $t('category.selectCategory') }}</option>
          <option 
            v-for="category in categories" 
            :key="category.name"
            :value="category.name"
          >
            {{ category.icon }} {{ category.name }}
          </option>
        </select>
      </div>

      <!-- 未匹配时显示创建新分类按钮 -->
      <div v-if="showCreateButton" class="create-category-wrapper">
        <button class="btn-create-category" @click="showCreateDialog = true" :title="$t('category.applyNew')">
          {{ $t('category.applyNew') }}
        </button>
      </div>
    </div>

    <!-- 创建新分类对话框 -->
    <div v-if="showCreateDialog" class="dialog-overlay" @click="showCreateDialog = false">
      <div class="dialog-content" @click.stop>
        <div class="dialog-header">
          <h3>{{ $t('category.create') }}</h3>
          <button class="dialog-close" @click="showCreateDialog = false">×</button>
        </div>
        <CreateCategoryDialog
          :product-name="productName"
          :user-id="currentUserId"
          @cancel="showCreateDialog = false"
          @submit-success="handleCreateSuccess"
        />
      </div>
    </div>

    <!-- 推荐理由 -->
    <div v-if="recommendation && recommendation.reason" class="recommend-reason">
      {{ recommendation.reason }}
    </div>
  </div>
</template>

<script>
import { getCategoryList, recommendCategory } from '@/api/product';
import CreateCategoryDialog from '@/components/CreateCategoryDialog.vue';

export default {
  name: 'CategorySelect',
  
  components: {
    CreateCategoryDialog
  },
  
  props: {
    value: {
      type: String,
      default: ''
    },
    productName: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    userId: {
      type: Number,
      default: 0
    }
  },
  
  emits: ['input'],
  
  data() {
    return {
      categories: [],
      recommendation: null,
      manualMode: false,
      recommendTimer: null,
      showCreateDialog: false,
      currentUserId: this.userId || 1,
      localValue: this.value || ''
    };
  },
  watch: {
    value(newVal) {
      console.log('[CategorySelect] value 变化:', newVal);
      this.localValue = newVal;
    },
    productName(newVal) {
      console.log('[CategorySelect] productName changed:', newVal);
      this.debouncedRecommend();
    }
  },
  
  mounted() {
    this.loadCategories();
  },
  
  methods: {
    async loadCategories() {
      try {
        const res = await getCategoryList();
        this.categories = res.data || res || [];
      } catch (error) {
        console.error('加载分类列表失败:', error);
        this.categories = [
          { name: '水果类', icon: '🍎' },
          { name: '蔬菜类', icon: '🥬' },
          { name: '肉类', icon: '🥩' },
          { name: '水产类', icon: '🐟' },
          { name: '粮油类', icon: '🍚' },
          { name: '调味品类', icon: '🧂' },
          { name: '乳品饮料', icon: '🥛' },
          { name: '休闲食品', icon: '🍞' },
          { name: '冷冻食品', icon: '🧊' },
          { name: '其他', icon: '📦' }
        ];
      }
    },

    debouncedRecommend() {
      if (this.recommendTimer) {
        clearTimeout(this.recommendTimer);
      }
      
      this.recommendTimer = setTimeout(() => {
        this.getRecommendation();
      }, 500);
    },

    async getRecommendation() {
      console.log('[CategorySelect] 获取推荐，productName:', this.productName);
      
      if (!this.productName || this.productName.trim().length < 2) {
        console.log('[CategorySelect] 商品名称太短，不推荐');
        this.recommendation = null;
        return;
      }
      
      try {
        console.log('[CategorySelect] 调用 API:', this.productName);
        const res = await recommendCategory(this.productName);
        console.log('[CategorySelect] API 响应:', res);
        this.recommendation = res.data || res;
        
        if (this.recommendation && this.recommendation.categoryName && (!this.value || this.value.trim() === '')) {
          this.$emit('input', this.recommendation.categoryName);
          this.manualMode = true;
        }
      } catch (error) {
        console.error('获取分类推荐失败:', error);
        this.recommendation = null;
      }
    },

    useRecommend() {
      if (this.recommendation) {
        this.$emit('input', this.recommendation.categoryName);
        this.manualMode = false;
      }
    },

    getConfidenceLevel() {
      if (!this.recommendation) return 'low';
      if (this.recommendation.confidence >= 80) return 'high';
      if (this.recommendation.confidence >= 50) return 'medium';
      return 'low';
    },

    handleCreateSuccess() {
      this.showCreateDialog = false;
      this.loadCategories();
    },

    showCreateButton() {
      return this.recommendation && 
             this.recommendation.confidence === 0 && 
             !this.manualMode &&
             !this.disabled;
    }
  }
};
</script>

<style scoped>
.category-select {
  position: relative;
  width: 100%;
}

.category-wrapper {
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
}

.category-dropdown {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.recommend-hint {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.recommend-hint.confidence-high {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px solid #86efac;
}

.recommend-hint.confidence-medium {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 2px solid #93c5fd;
}

.recommend-hint.confidence-low {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 2px solid #fcd34d;
}

.recommend-animation {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
}

.robot-icon {
  font-size: 20px;
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

.sparkle {
  font-size: 16px;
  animation: sparkle 1.5s infinite;
}

@keyframes sparkle {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
}

.recommend-content {
  flex: 1;
}

.recommend-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.ai-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.recommend-text {
  font-size: 14px;
  color: #333;
}

.recommend-details {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.confidence-bar {
  width: 100px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.confidence-fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #fbbf24, #10b981);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.confidence-text {
  font-size: 11px;
  color: #6b7280;
}

.matched-keywords {
  font-size: 11px;
  color: #6b7280;
}

.keyword-tag {
  display: inline-block;
  background: rgba(0,0,0,0.05);
  padding: 2px 6px;
  border-radius: 4px;
  margin: 0 2px;
}

.change-btn {
  background: none;
  border: 1px solid #d1d5db;
  color: #6b7280;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.change-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.category-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  transition: border-color 0.2s;
  box-sizing: border-box;
  height: 42px;
}

.category-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.category-input:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

.create-category-wrapper {
  flex-shrink: 0;
}

.btn-create-category {
  padding: 11px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  height: 42px;
  display: flex;
  align-items: center;
}

.btn-create-category:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.recommend-reason {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 6px;
  font-size: 12px;
  color: #6b7280;
  border-left: 3px solid #667eea;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.dialog-content {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: auto;
}

.dialog-header {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-header h3 {
  margin: 0;
  font-size: 16px;
  color: #111827;
}

.dialog-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6b7280;
}
</style>
